import AdditionalScenarioInformationPdf from 'components/v2/compiler/grids/scenarios/AdditionalScenarioInformationPdf';
import DefaultScenarioInformationPdf from 'components/v2/compiler/grids/scenarios/DefaultScenarioInformationPdf';
import ModuleGridPdf from 'components/v2/compiler/grids/scenarios/ModuleGridPdf';
import React from 'react';
import DebugOnly from 'util/dev-tools/DebugOnly';
import RenderCounter from 'util/dev-tools/RenderCounter';
import { PdfGridsArea, PdfGridsAreaBreak, PdfH3, PdfPage } from './Styles.styled';
import usePdfData from './utils/usePdfData';
import { VersionStateType } from 'services/EpdClient';

const ScenariosPage: React.FC = () => {
  const { epdVersionId, epdDefinition, scenarios, systemBoundaryGrid: declaredModules } = usePdfData();
  const isEPDComplete =
    epdDefinition.versionState === VersionStateType.Published ||
    epdDefinition.versionState === VersionStateType.Deregistered;

  return (
    <PdfPage>
      <DebugOnly>
        <RenderCounter componentName="ScenariosPage" threshold={3} />
      </DebugOnly>

      {scenarios.map((scenario, index) => (
        <PdfGridsAreaBreak key={index}>
          <PdfGridsArea>
            {scenario.isDefaultScenario ? (
              <DefaultScenarioInformationPdf scenario={scenario} />
            ) : (
              <AdditionalScenarioInformationPdf scenario={scenario} index={index} />
            )}

            {declaredModules?.declaredModules?.a4 && (
              <ModuleGridPdf
                epdVersionId={epdVersionId}
                scenarioId={scenario?.id}
                module="A4"
                moduleName="A4"
                moduleSectionTitle="Module A4: Transport to the building site"
                isDefaultScenario={scenario.isDefaultScenario}
                isEPDComplete={isEPDComplete}
              />
            )}

            {declaredModules?.declaredModules?.a5 && (
              <ModuleGridPdf
                epdVersionId={epdVersionId}
                scenarioId={scenario?.id}
                module="A5"
                moduleName="A5"
                moduleSectionTitle="Module A5: Installation in the building"
                isDefaultScenario={scenario.isDefaultScenario}
                isEPDComplete={isEPDComplete}
              />
            )}
          </PdfGridsArea>

          <PdfGridsArea>
            {declaredModules?.declaredModules?.b1 && (
              <ModuleGridPdf
                epdVersionId={epdVersionId}
                scenarioId={scenario?.id}
                module="B1"
                moduleName="B1"
                moduleSectionTitle="Module B1: Use"
                isDefaultScenario={scenario.isDefaultScenario}
                isEPDComplete={isEPDComplete}
              />
            )}
            {declaredModules?.declaredModules?.b2 && (
              <ModuleGridPdf
                epdVersionId={epdVersionId}
                scenarioId={scenario?.id}
                module="B2"
                moduleName="B2"
                moduleSectionTitle="Module B2: Maintenance"
                isDefaultScenario={scenario.isDefaultScenario}
                isEPDComplete={isEPDComplete}
              />
            )}
            {declaredModules?.declaredModules?.b3 && (
              <ModuleGridPdf
                epdVersionId={epdVersionId}
                scenarioId={scenario?.id}
                module="B3"
                moduleName="B3"
                moduleSectionTitle="Module B3: Repair"
                isDefaultScenario={scenario.isDefaultScenario}
                isEPDComplete={isEPDComplete}
              />
            )}
          </PdfGridsArea>

          <PdfGridsArea>
            {declaredModules?.declaredModules?.b4 && (
              <ModuleGridPdf
                epdVersionId={epdVersionId}
                scenarioId={scenario?.id}
                module="B4"
                moduleName="B4"
                moduleSectionTitle="Module B4: Replacement"
                isDefaultScenario={scenario.isDefaultScenario}
                isEPDComplete={isEPDComplete}
              />
            )}
            {declaredModules?.declaredModules?.b5 && (
              <ModuleGridPdf
                epdVersionId={epdVersionId}
                scenarioId={scenario?.id}
                module="B5"
                moduleName="B5"
                moduleSectionTitle="Module B5: Refurbishment"
                isDefaultScenario={scenario.isDefaultScenario}
                isEPDComplete={isEPDComplete}
              />
            )}
          </PdfGridsArea>

          <PdfGridsArea>
            {scenario?.enabledRSL && (
              <ModuleGridPdf
                epdVersionId={epdVersionId}
                scenarioId={scenario?.id}
                module="RSL"
                moduleName="Reference service life"
                moduleSectionTitle="Reference service life"
                isDefaultScenario={scenario.isDefaultScenario}
                isEPDComplete={isEPDComplete}
              />
            )}
            {scenario?.enabledRSL == null && <PdfH3>Reference service life</PdfH3>}
            {declaredModules?.declaredModules?.b6 && (
              <ModuleGridPdf
                epdVersionId={epdVersionId}
                scenarioId={scenario?.id}
                module="B6"
                moduleName="B6"
                moduleSectionTitle="Module B6: Operational energy use"
                isDefaultScenario={scenario.isDefaultScenario}
                isEPDComplete={isEPDComplete}
              />
            )}
          </PdfGridsArea>
          <PdfGridsArea>
            {declaredModules?.declaredModules?.b7 && (
              <ModuleGridPdf
                epdVersionId={epdVersionId}
                scenarioId={scenario?.id}
                module="B7"
                moduleName="B7"
                moduleSectionTitle="Module B7: Operational water use"
                isDefaultScenario={scenario.isDefaultScenario}
                isEPDComplete={isEPDComplete}
              />
            )}
            {(declaredModules?.declaredModules?.c1 ||
              declaredModules?.declaredModules?.c2 ||
              declaredModules?.declaredModules?.c3 ||
              declaredModules?.declaredModules?.c4) && (
              <ModuleGridPdf
                epdVersionId={epdVersionId}
                scenarioId={scenario?.id}
                module="C1"
                moduleName="C"
                moduleSectionTitle="Module C: End-of-life"
                isDefaultScenario={scenario.isDefaultScenario}
                isEPDComplete={isEPDComplete}
              />
            )}
            {declaredModules?.declaredModules?.d && (
              <ModuleGridPdf
                epdVersionId={epdVersionId}
                scenarioId={scenario?.id}
                module="D"
                moduleName="D"
                moduleSectionTitle="Module D: Beyond product life cycle"
                isDefaultScenario={scenario.isDefaultScenario}
                isEPDComplete={isEPDComplete}
              />
            )}
          </PdfGridsArea>
        </PdfGridsAreaBreak>
      ))}
    </PdfPage>
  );
};

export default ScenariosPage;
